import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";

import { routes } from "../constants/routes";
import { getOrCreateDeviceId } from '@/app/_module/services/auth';
import { getApiUrl } from "./config";

import AgencyTokenManager from './agencyTokenManager';
import config from "./config";
import { AGENCY_ENDPOINTS, EXCLUDED_AGENCY_ENDPOINTS } from "./endpoints";
import supabaseAuthService from './supabaseAuthService';
import { getRateLimitMessage } from "../utils/rate-limit";
import { showToast } from "../utils/show-toast";
import { isDemoMode } from "../utils/demoMode";
import { isDemoHostname } from "@/app/_module/utils/demoMode";
import { canReplayAfterDbBlip } from "./dbBlipRetry";

const baseURL = config.baseUrl;

// Type for the session object
// eslint-disable-next-line no-restricted-syntax
interface SessionData {
  user: {
    accessToken?: string;
    [key: string]: unknown;
  };
  expires: string;
  [key: string]: unknown;
}

const axiosInstance = axios.create({
  baseURL,
  timeout: 60000, // 60 second timeout for AI operations (first message can take 30-45s with cold start)
  // Removed withCredentials: true - using JWT tokens instead of httpOnly cookies
  headers: {
    // REMOVED: "Content-Type": "application/json" - This was breaking FormData uploads
    Accept: "application/json, text/plain, */*",
    // REMOVED CORS headers - these should only be sent by server, not client
    // The client should not send Access-Control-* headers in requests
  },
});

// Helper function to check if URL should use agency token
const shouldUseAgencyToken = (url: string): boolean => {
  // Check if URL matches any agency endpoint pattern
  const isAgencyEndpoint = AGENCY_ENDPOINTS.some(pattern => url.includes(pattern));
  
  // Check if URL is in excluded list
  const isExcluded = EXCLUDED_AGENCY_ENDPOINTS.some(pattern => url.includes(pattern));
  
  return isAgencyEndpoint && !isExcluded;
};

// Removed token refresh logic: session is cookie-managed by backend

const onRequest = async (
  request: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> => {
  if (!request.headers) return request;
  
  // Enhanced debugging for specific requests
  const isA2PRequest = request.url?.includes('/communication/a2p/');
  const isDashboardRequest = request.url?.includes('/dashboard/');
  const isPremiumRequest = request.url?.includes('/premium-sales/');

  try {
    // Check if authentication should be skipped
    const skipAuth = request.headers?.['x-skip-auth'] === 'true';
    if (skipAuth) {
      // Remove the skip-auth header before sending to backend
      delete request.headers['x-skip-auth'];
    } else {
      // PROACTIVE TOKEN REFRESH: Check if token is about to expire and refresh BEFORE the API call.
      // Chrome OS and mobile browsers throttle setInterval timers when idle/backgrounded,
      // which can prevent Supabase's auto-refresh from firing. This ensures every API call
      // has a fresh token, even if the background timer was throttled.
      try {
        const session = await supabaseAuthService.getCurrentSession();
        if (session?.expires_at) {
          const expiresAt = session.expires_at * 1000;
          const now = Date.now();
          const fiveMinutes = 5 * 60 * 1000;
          // If token expires within 5 minutes, proactively refresh
          if (expiresAt - now < fiveMinutes && expiresAt > now) {
            await supabaseAuthService.refreshSession();
          }
        }
      } catch {
        // Non-fatal — getAccessToken below will still try
      }

      // Use Supabase authentication tokens
      let supabaseToken = await supabaseAuthService.getAccessToken();

      // ⚠️ WAIT FOR REHYDRATION BEFORE GIVING UP.
      //
      // Supabase restores its session from storage ASYNCHRONOUSLY. A request
      // fired in that window gets no token, goes out unauthenticated, and is a
      // GUARANTEED 401 — which the response interceptor below then treats as a
      // dead session and force-redirects to /auth/sign-in?reason=session_expired.
      //
      // On desktop the app usually wins that race. In the iOS WKWebView it does
      // NOT: reproduced in the simulator 2026-08-25, where /api/branding/my-agency
      // and /api/dialer-preferences both left /m/dashboard with NO authorization
      // header while a perfectly valid token (56 min of life left) sat in
      // localStorage. The user signed in, passed 2FA, saw the dashboard for a
      // beat, and was thrown back to the login screen.
      //
      // This cannot weaken auth: it only retries READING a token we already own.
      // If there genuinely is no session, every retry returns null too and the
      // request proceeds unauthenticated exactly as before.
      if (!supabaseToken) {
        for (const delayMs of [150, 400, 800]) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
          supabaseToken = await supabaseAuthService.getAccessToken();
          if (supabaseToken) break;
        }
      }

      if (supabaseToken) {
        request.headers.Authorization = `Bearer ${supabaseToken}`;
      } else {
        // No token available - this will cause 401, which is handled by error interceptor

        if (isA2PRequest) {

        }
      }
    }
    
  // SINGLE-SESSION / SELF-EVICTION FIX (2026-07-24): send the stable device id on
  // EVERY request, not just /auth/profile. The backend scopes single-session
  // enforcement by device, and when this header is absent it has to fall back to a
  // derived id (`${userId}-${browser}-${os}`). One browser then presented TWO
  // identities (localStorage UUID on /auth/profile, derived id everywhere else),
  // so the user's own tabs looked like two devices and evicted each other in a
  // logout loop. Always sending it keeps one browser = one device.
  try {
    // getOrCreateDeviceId() persists a stable UUID rather than returning null when
    // the key is missing (e.g. after the devtools-protection wipe). That matters
    // now that MFA_API_ENFORCEMENT refuses MFA-enabled accounts with no device
    // header — a browser that lost the key would otherwise be locked out instead
    // of simply re-prompted. Still ONE id per browser, so no phantom devices.
    const deviceId = getOrCreateDeviceId();
    if (deviceId) {
      request.headers['x-device-id'] = deviceId;
    }
  } catch {
    // localStorage unavailable (private mode / SSR) — backend falls back safely.
  }

  // Inject active agency context for backend authorization (always, if available)
  // Note: AgencyTokenManager.getCurrentAgencyId() now prioritizes the current agency data
  // which is updated immediately when users switch agencies
  let currentAgencyId = AgencyTokenManager.getCurrentAgencyId();

  // Only use user context fallback if AgencyTokenManager returns nothing
  // This should rarely happen, as agency data is set during login and agency switching
  let isSystemAdmin = false;
  if (!currentAgencyId) {
    try {
      const userContext = sessionStorage.getItem('insuracentral_user_context');
      if (userContext) {
        const parsed = JSON.parse(userContext);
        currentAgencyId = parsed.agencyId;
        isSystemAdmin = parsed.userType === 'admin'; // System Admins have userType: 'admin'
      }
    } catch (error) {

    }
  }

  if (currentAgencyId) {
    // Backend expects lowercase 'x-agency-id' header
    request.headers['x-agency-id'] = currentAgencyId;
  } else {
    // Only warn for protected requests that require agency context
    // IMPORTANT: System Admins don't have an agency - skip warning for them
    if (request.url &&
        !request.url.includes('/auth/') &&
        !request.url.includes('/api/health') &&
        !request.url.includes('/config/') &&
        !isSystemAdmin) {
    }
  }

  // Enhanced telemetry with full header analysis
  if (request.url) {
    const requestInfo = {
      method: request.method || 'GET',
      url: request.url,
      hasAuth: !!request.headers.Authorization,
      agencyId: currentAgencyId || null,
      authHeader: request.headers.Authorization ? 'Bearer [TOKEN]' : 'NONE',
      agencyHeader: request.headers['x-agency-id'] || 'NONE',
      allHeaders: Object.keys(request.headers || {})
    };
  }
  } catch (error) {

  }

  if (request.data && !(request.data instanceof FormData)) {
    request.headers['Content-Type'] = 'application/json';
  }

  return request;
};

// Removed getLocalStorageToken - only using Supabase ES256 tokens now

const onRequestError = async (error: AxiosError): Promise<AxiosError> => {
  return await Promise.reject(error);
};

const onResponse = (response: AxiosResponse): AxiosResponse => {
  // Dispatch custom event on successful API responses to indicate user activity
  // This helps reset the auto-logout timer even when user is just reading data without mouse/keyboard input
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('api-activity'));

    // Recovery edge: we were being refused for MFA and now we are not.
    //
    // Fires ONCE on the transition, never on every success — 'mfa-satisfied' triggers a
    // full React Query invalidation, so dispatching it per response would refetch forever.
    if (mfaBlocked) {
      mfaBlocked = false;
      if (mfaProbeTimer) {
        clearInterval(mfaProbeTimer); // real traffic recovered first; stop polling
        mfaProbeTimer = null;
      }
      // Retracts the overlay AND refetches everything that failed while blocked. The
      // second part is why a hard refresh used to be needed: the dashboard's providers
      // fetch once on mount, and every one of them 403'd during the login window, so the
      // screen stayed empty even after the session became valid.
      window.dispatchEvent(new CustomEvent('mfa-satisfied'));
    }
  }
  return response.data ?? response;
};

/**
 * MFA gate state for the login window.
 *
 * Password sign-in mints a Supabase session BEFORE the 2FA code is entered, and the app's
 * global providers start fetching immediately. Those calls correctly 403 until the code is
 * verified, which is a transient race, not a real lockout — so the prompt is scheduled
 * rather than shown, and cancelled if a successful response arrives first.
 */
let mfaBlocked = false;

let mfaProbeTimer: ReturnType<typeof setInterval> | null = null;
const MFA_PROBE_INTERVAL_MS = 3_000;
const MFA_PROBE_MAX_ATTEMPTS = 40; // ~2 minutes

/**
 * Actively poll until the gate opens, then let onResponse fire the recovery edge.
 *
 * ⚠️ WHY THIS EXISTS — do not replace it with "recover on the next successful response".
 * That was the first attempt and it cannot work: React Query is configured `retry: 1`, so
 * every dashboard provider that 403s during the login window gives up about a second later.
 * With nothing left in flight, no response ever arrives, the recovery edge never fires, and
 * the screen stays empty until a manual refresh — which is exactly the bug this is meant to
 * fix. Recovery has to generate its own traffic.
 *
 * Uses the raw axios instance so a probe that 403s does not itself schedule a prompt or
 * recurse into this scheduler.
 */
const startMfaProbe = () => {
  if (mfaProbeTimer || typeof window === 'undefined') return;
  let attempts = 0;

  mfaProbeTimer = setInterval(async () => {
    attempts += 1;
    if (!mfaBlocked || attempts > MFA_PROBE_MAX_ATTEMPTS) {
      if (mfaProbeTimer) clearInterval(mfaProbeTimer);
      mfaProbeTimer = null;
      return;
    }
    try {
      const token = await supabaseAuthService.getAccessToken();
      if (!token) return;
      const res = await fetch(`${baseURL}/users/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) return; // still gated, keep waiting

      // The gate let us through. Clear state and tell the app to refetch what it lost.
      // 'mfa-satisfied' also retracts the overlay, which is what makes showing the
      // prompt on the blocking edge safe — a transient block corrects itself here.
      mfaBlocked = false;
      if (mfaProbeTimer) clearInterval(mfaProbeTimer);
      mfaProbeTimer = null;
      window.dispatchEvent(new CustomEvent('mfa-satisfied'));
    } catch {
      /* network blip — try again next tick */
    }
  }, MFA_PROBE_INTERVAL_MS);
};

// Global deduplication: when multiple requests get 401 simultaneously,
// only ONE refresh attempt runs at a time. Others wait on the same Promise.
let globalRefreshPromise: Promise<any> | null = null;
let isSigningOut = false;

// Enhanced response error handler with better token refresh logic and retry strategy
const onResponseError = async (error: AxiosError) => {
  const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean; _retryCount?: number; _dbBlipRetries?: number };
  const statusCode = error.response?.status;

  // Database blip: they clear in a second or two, so the request retries quietly (1s,
  // then 2s) before the page sees it. Reads always; writes only when the server says
  // none of the request ran (canReplayAfterDbBlip).
  if (originalRequest && canReplayAfterDbBlip(statusCode, error.response?.data, originalRequest.method)) {
    const tries = originalRequest._dbBlipRetries ?? 0;
    if (tries < 2) {
      originalRequest._dbBlipRetries = tries + 1;
      await new Promise((resolve) => setTimeout(resolve, 1000 * (tries + 1)));
      return axiosInstance(originalRequest);
    }
  }

  // ACCOUNT SUSPENDED — the guard marks these 403s with error:'ACCOUNT_SUSPENDED'
  // and the admin-set reason. Broadcast once so the full-screen lockout overlay
  // (AccountSuspendedOverlay, mounted in the authenticated layout) takes over
  // instead of every screen toasting its own error.
  if (
    statusCode === 403 &&
    (error.response?.data as any)?.error === 'ACCOUNT_SUSPENDED' &&
    typeof window !== 'undefined'
  ) {
    window.dispatchEvent(
      new CustomEvent('account-suspended', {
        detail: (error.response?.data as any)?.message || 'Your account has been suspended.',
      }),
    );
  }

  // DIRECT 130 CALLING DEBT — a call can take the wallet below $0, and more than $10 owed closes the CRM until it's
  // paid (guard: 402 DIAL_CREDITS_OVERDUE on everything but the credits routes). Same pattern as ACCOUNT_SUSPENDED:
  // one overlay (DialCreditsOverdueOverlay) takes over.
  if (
    statusCode === 402 &&
    (error.response?.data as any)?.error === 'DIAL_CREDITS_OVERDUE' &&
    typeof window !== 'undefined'
  ) {
    window.dispatchEvent(
      new CustomEvent('dial-credits-overdue', { detail: (error.response?.data as any)?.message || '' }),
    );
  }

  // MFA REQUIRED — with MFA_API_ENFORCEMENT on, the backend refuses data
  // endpoints for an MFA-enabled account whose second factor hasn't been
  // completed on this device (or whose 12h marker lapsed while idle). Without
  // this branch that surfaces as unexplained errors all over the app; broadcast
  // it so one handler can send the user back to finish MFA. Same shape as
  // ACCOUNT_SUSPENDED above.
  if (
    statusCode === 403 &&
    (error.response?.data as any)?.error === 'MFA_REQUIRED' &&
    typeof window !== 'undefined'
  ) {
    // NO USER-FACING PROMPT. Deliberate — do not add one back.
    //
    // Nothing here weakens the gate: the 403 still happens, the request still fails,
    // and the server-side check is untouched. This only decides what the UI does about
    // it — and the answer is "recover silently", not "interrogate the user".
    //
    // MFA is enforced at SIGN-IN, so nobody reaches an authenticated screen without
    // having already passed their second factor. A 403 MFA_REQUIRED here therefore
    // never means "this person skipped 2FA" — it means OUR marker for their session
    // went missing (session rotation, a cold per-instance cache, an expired memory
    // window). Showing "Verify it's you" and forcing a full re-login made the user
    // pay for a bookkeeping failure on our side, and threw away whatever they were
    // in the middle of.
    //
    // What happens instead: startMfaProbe polls until the gate opens, then the
    // recovery edge in onResponse fires 'mfa-satisfied', which refetches everything
    // that failed. The screen fills in on its own with no interruption. Server-side,
    // the guard's L4 device fallback re-stamps the rotated session on the first
    // request, so the usual case clears in one poll.
    mfaBlocked = true;
    startMfaProbe();
  }

  // DEMO SANDBOX — the backend marks blocked actions (upload, purchase, billing,
  // password/email/2FA) with errorCode:'DEMO_RESTRICTED'. Broadcast so the trial prompt
  // takes over: a visitor hitting a wall is the moment to offer the trial, not to show
  // them a red error toast. Same pattern as ACCOUNT_SUSPENDED above.
  if (
    statusCode === 403 &&
    (error.response?.data as any)?.error === 'DEMO_RESTRICTED' &&
    typeof window !== 'undefined'
  ) {
    window.dispatchEvent(
      new CustomEvent('demo-restricted', {
        detail: (error.response?.data as any)?.message || '',
      }),
    );
  }

  // Enhanced error logging for debugging backend issues (dev only)
  const isA2PRequest = originalRequest?.url?.includes('/communication/a2p/');
  const isDashboardRequest = originalRequest?.url?.includes('/dashboard/');
  const isPremiumRequest = originalRequest?.url?.includes('/premium-sales/');
  const isPurchaseHistoryRequest = originalRequest?.url?.includes('purchase-history');

  // DEBUG: Log purchase-history errors
  if (isPurchaseHistoryRequest) {
  }

  // Special A2P error debugging
  if (isA2PRequest && statusCode === 401) {

  }

  // ── Rate limiting (HTTP 429) ─────────────────────────────────────────────
  // Centralized so EVERY throttled endpoint surfaces a clear "cool down"
  // message instead of a generic "failed". The backend's custom guards return
  // a descriptive sentence (used verbatim); the generic NestJS ThrottlerGuard
  // returns "Too Many Requests" → replaced with a friendly cool-down.
  //
  // CRITICAL: we reject an actual Error INSTANCE (not a plain object). ~88
  // component catch blocks use `error instanceof Error ? error.message : "Failed
  // to ..."`. A plain object fails that check and falls through to the hardcoded
  // "Failed ..." string — so a 429 would still read as "failed". An Error whose
  // `.message` is the cool-down text makes all of those show the right message.
  // We attach { statusCode, code, isRateLimit, retryAfterSeconds, data } so the
  // 429-aware handlers (e.g. the CSV import progress box) can still detect it.
  //
  // We toast ONCE here. showToast() dedupes by message, so a component
  // re-toasting the same string won't double up. Background/polling requests
  // (typing, presence, unread, etc.) are throttle-silent — no toast.
  if (statusCode === 429) {
    const data = error.response?.data;
    const base: Record<string, any> =
      data && typeof data === 'object' ? { ...(data as Record<string, any>) } : {};
    if (typeof data === 'string' && data) base.message = data;
    const retryHeader = error.response?.headers?.['retry-after'];
    if (base.retryAfterSeconds == null && retryHeader != null) {
      base.retryAfterSeconds = Number(retryHeader);
    }

    const friendly = getRateLimitMessage(base);
    const rateErr = new Error(friendly) as Error & Record<string, any>;
    rateErr.statusCode = 429;
    rateErr.code = '429';
    rateErr.isRateLimit = true;
    rateErr.retryAfterSeconds = base.retryAfterSeconds;
    rateErr.data = base;

    const url = String(originalRequest?.url || '');
    const isBackground =
      /typing|presence|heartbeat|read-receipt|\/seen\b|online-status|unread|poll/i.test(
        url,
      );
    if (!isBackground) {
      showToast(friendly, 'warning');
    }
    return await Promise.reject(rateErr);
  }

  if (statusCode === 401) {
    // Skip session refresh for auth endpoints - they handle their own auth errors
    // This prevents the interceptor from redirecting when 2FA code is wrong, etc.
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/');
    if (isAuthEndpoint) {
      return await Promise.reject(error);
    }

    // Skip session refresh for vendor portal endpoints - they use separate session tokens
    // Vendor dashboard handles its own 401 errors and redirects to /vendor/login
    const isVendorEndpoint = originalRequest?.url?.includes('/v1/vendor/') || originalRequest?.url?.includes('/api/v1/vendor/');
    if (isVendorEndpoint) {
      return await Promise.reject(error);
    }

    // Initialize retry count
    const retryCount = originalRequest?._retryCount ?? 0;
    const maxRetries = 3; // Allow up to 3 refresh attempts before giving up (server can be slow under load)

    // Try to refresh the Supabase session before redirecting
    if (retryCount < maxRetries) {
      try {
        // Exponential backoff: 1s, 2s, 4s — longer delays to tolerate slow backend responses
        if (retryCount > 0) {
          await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retryCount - 1)));
        }

        // DEDUP: If another 401 handler is already refreshing, wait on its result
        // instead of firing a second concurrent refresh to Supabase
        if (!globalRefreshPromise) {
          globalRefreshPromise = supabaseAuthService.refreshSession().finally(() => {
            globalRefreshPromise = null;
          });
        }
        const refreshedSession = await globalRefreshPromise;

        if (refreshedSession) {
          originalRequest._retry = true;
          originalRequest._retryCount = retryCount + 1;
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${refreshedSession.access_token}`;
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        // Check if this is a network error vs a real auth error
        const isNetworkError = !navigator.onLine ||
          (refreshError as any)?.code === 'NETWORK_ERROR' ||
          (refreshError as any)?.message?.includes('network');

        // If it's a network error and we have retries left, try again with longer wait
        if (isNetworkError && retryCount < maxRetries - 1) {
          originalRequest._retryCount = retryCount + 1;
          await new Promise(resolve => setTimeout(resolve, 2000 * (retryCount + 1))); // 2s, 4s
          return axiosInstance(originalRequest);
        }
      }
    }

    // "All refresh attempts failed" is NOT the same as "the session is invalid".
    //
    // refreshSession() RETURNS null on a transport failure rather than throwing,
    // so the catch above never fires for an unreachable auth server — execution
    // falls straight through here and signs the user out. That is one of the two
    // paths that booted ~30 agents during the 2026-08-28 Supabase pooler outage
    // (16:36-16:37 UTC, :econnrefused). Their tokens were fine; we just could not
    // reach the server to renew them.
    //
    // A BLIP SHOULD BE INVISIBLE, not just survivable. Keeping the session is the
    // safety net; riding out the outage is the actual goal. Most of these are a
    // pooler hiccup measured in seconds, so reuse the same retry ladder the
    // network-error path above uses (2s, 4s) before surfacing anything to the
    // agent. Only once the ladder is exhausted do we give up on THIS request —
    // and even then the session stays, so the next click works.
    if (supabaseAuthService.lastRefreshWasTransportFailure()) {
      if (retryCount < maxRetries - 1) {
        originalRequest._retryCount = retryCount + 1;
        console.warn(
          `[axios] Auth server unreachable — retry ${retryCount + 1}/${maxRetries - 1} in ${2 * (retryCount + 1)}s, session kept.`,
        );
        await new Promise(resolve => setTimeout(resolve, 2000 * (retryCount + 1)));
        return axiosInstance(originalRequest);
      }

      console.warn(
        '[axios] Token refresh could not reach the auth server — keeping session, not signing out.',
      );
      return await Promise.reject(
        new Error('Could not reach the server to refresh your session. Please retry.'),
      );
    }

    // If all refresh attempts failed, the session is truly invalid
    // PROTECT ACTIVE CALLS: Don't force-redirect if user is on a call or dialer session
    // The call runs through Twilio (independent of our API session), so it stays alive
    // We'll just reject the request and let the call continue — session re-validates after call ends
    if (typeof window !== 'undefined') {
      try {
        const isOnActiveCall = sessionStorage.getItem('insuracentral_active_call') === 'true'
          || localStorage.getItem('insuracentral_active_call') === 'true';
        if (isOnActiveCall) {
          return await Promise.reject(new Error('Session expired but call is active. Will re-authenticate after call.'));
        }
      } catch {
        // sessionStorage access failed — proceed with normal logout
      }
    }

    // SANDBOX: /demo/enter is mid-way through signing itself in — it has no session yet,
    // on purpose. Other components on the page fire authenticated requests while the seat
    // claim is in flight, and their 401s land here. This must return BEFORE the signOut
    // below, because that clears the very session the page is about to install (and then
    // the redirect sends the visitor to a login screen they were never meant to see).
    //
    // /demo (the landing page) is covered by the same prefix and needs it for a different
    // reason: it is PUBLIC and has no session by design. Falling through would hit the
    // demo branch further down and bounce a visitor who is still reading the page into
    // /demo/enter — silently burning a seat they never asked for.
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/demo')) {
      return await Promise.reject(new Error('Demo session is still being created.'));
    }

    // DEDUP: Only one signOut at a time — prevents cascade of 10 concurrent signOuts
    // when multiple requests all fail their refresh retries simultaneously
    if (!isSigningOut) {
      isSigningOut = true;
      try {
        // LOCAL scope: this fires when THIS device's session is already dead
        // (401s / refresh failed — e.g. it was evicted by a login elsewhere).
        // A global signOut here would revoke the NEW device's tokens and
        // ping-pong both sessions dead.
        await supabaseAuthService.signOut({ scope: 'local' });
      } catch (signOutError) {
        // Silently handle
      } finally {
        isSigningOut = false;
      }
    }

    // Redirect to login page
    // For admin panel: dispatch event so the admin session monitor can handle it gracefully
    // instead of a jarring hard redirect
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      const isAdminPanel = currentPath.includes('/admin-panel');
      if (isAdminPanel) {
        window.dispatchEvent(new CustomEvent('admin-session-expired'));
        return await Promise.reject(new Error('Session expired. Please re-authenticate.'));
      }
      // Don't redirect vendor, marketing, invite, affiliate, or agent landing pages to main sign-in
      if (currentPath.startsWith('/vendor') || currentPath.startsWith('/marketing') || currentPath.startsWith('/invite') || currentPath.startsWith('/affiliate')) {
        return await Promise.reject(new Error('Session expired.'));
      }
      // /share is the public QR hand-off page — the scanning phone is anonymous by
      // design, so a 401 from any stray API call must never bounce it to sign-in.
      if (currentPath === '/share' || currentPath.startsWith('/share/')) {
        return await Promise.reject(new Error('Session expired.'));
      }
      // /oauth/bridge ("Sign in with InsuraCentral" for the forum / Licensedproducer)
      // probes for a live session and EXPECTS a 401 when there is none: it then sends
      // the visitor to sign-in itself, with the bridge URL as the return target. The
      // generic redirect here raced it and won, landing people on the dashboard after
      // login instead of back on the site they came from.
      if (currentPath.startsWith('/oauth/bridge')) {
        return await Promise.reject(new Error('Not signed in.'));
      }
      // Agent landing pages use subdomains — don't redirect to sign-in
      // Excluding the sandbox host: it is not an agent landing page, and this branch sits
      // BEFORE the isDemoMode() re-enter handler below — so without the exclusion an
      // expired demo session returned a bare "Session expired" and the visitor was stuck
      // instead of being sent to /demo/enter for a fresh seat.
      const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
      if (!isDemoHostname(hostname) && hostname.endsWith('.insuracentral.com') && hostname !== 'app.insuracentral.com' && hostname !== 'www.insuracentral.com' && hostname !== 'insuracentral.com') {
        return await Promise.reject(new Error('Session expired.'));
      }
      // Affiliate/vendor/marketing portals have their own auth — don't redirect to CRM sign-in
      const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
      if (pathname.startsWith('/affiliate') || pathname.startsWith('/vendor') || pathname.startsWith('/marketing')) {
        return await Promise.reject(new Error('Session expired.'));
      }
      // SANDBOX: never show a demo visitor a login screen. They have no password and
      // never signed up, so "Your session has expired. Please sign in again." is a dead
      // end. Send them through /demo/enter instead, which claims a fresh seat and drops
      // them straight back into a working sandbox.
      if (isDemoMode()) {
        window.location.href = '/demo/enter';
        return await Promise.reject(new Error('Demo session renewed.'));
      }

      window.location.href = '/auth/sign-in?reason=session_expired';
    }

    return await Promise.reject(new Error('Session expired. Please sign in again.'));
  }

  // Handle other error types with better logging
  if (statusCode === 403) {
    const agencyId = originalRequest?.headers?.['x-agency-id'] as string | undefined;
    const errorData = error.response?.data as any;

    // Smart retry logic: Only retry if we didn't have an agency ID before
    const hadAgencyId = !!agencyId;
    
    if (!originalRequest?._retry && !hadAgencyId) {
      try {
        originalRequest._retry = true;

        // Don't call profile endpoint here to avoid circular dependency
        // Just check if agency context is available in storage
        
        const refreshedAgencyId = AgencyTokenManager.getCurrentAgencyId();
        
        if (refreshedAgencyId && refreshedAgencyId !== agencyId) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['x-agency-id'] = refreshedAgencyId;
          return axiosInstance(originalRequest);
        } else {

        }
      } catch (retryError) {

      }
    } else if (hadAgencyId) {
      // We already had an agency ID, so this is a real permission issue

    }
    
    // User-facing hint

  } else if (statusCode === 404) {

  } else if (statusCode && statusCode >= 500) {
    // Always log server errors (even in production) for monitoring

  } else if (statusCode === 0 || error.code === 'NETWORK_ERROR') {

  }

  // DO NOT LET THE MFA 403 BODY TRAVEL ONWARD.
  //
  // The MFA_REQUIRED branch above recovers silently on purpose — startMfaProbe polls
  // until the gate reopens and then refetches what failed, because a 403 here never
  // means "this person skipped 2FA" (sign-in enforces it); it means OUR marker for
  // their session went missing after a rotation. The user is signed in and should not
  // be interrupted.
  //
  // But rejecting with `error.response.data` handed every call site the SERVER's body,
  // whose `.message` is literally "Two-factor authentication is required before using
  // this account." 29 components call `errorToast(err.message || '...')`, so any request
  // in flight during that window printed that sentence as a red toast — which is exactly
  // the interruption the branch above was written to avoid. The silent recovery was
  // correct and deployed; it was being undone here.
  //
  // Rejecting with an EMPTY message keeps the rejection (callers still see a failure and
  // their own catch/onError still runs) while making `err.message || 'Failed to load X'`
  // fall through to each component's own sensible default.
  //
  // EMPTY STRING, NOT undefined — this was checked, not guessed. 21 call sites do
  // `err.message.includes(...)` / `.toLowerCase()`, which would throw a TypeError on
  // undefined. The only thing '' costs is that `err.message ?? 'default'` would yield a
  // blank toast instead of the default, and ZERO call sites use `??` here. If that ever
  // changes, fix the call site — do not switch this to undefined.
  if (statusCode === 403 && (error.response?.data as any)?.error === 'MFA_REQUIRED') {
    const mfaError: any = new Error('');
    mfaError.code = 'MFA_REQUIRED';
    mfaError.error = 'MFA_REQUIRED'; // keeps the old body's discriminator readable
    mfaError.statusCode = 403;
    mfaError.silent = true; // for call sites that want to skip their toast entirely
    return await Promise.reject(mfaError);
  }

  return await Promise.reject(error?.response?.data || error.message || error);
};

axiosInstance.interceptors.request.use(onRequest, onRequestError);
axiosInstance.interceptors.response.use(onResponse, onResponseError);

export const runtime = "nodejs";

export default axiosInstance;

// Force Node.js runtime for this module to avoid Edge Runtime issues with axios